import { FileSearchOutlined, InfoCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import Button from 'components/ui/base/button';
import Divider from 'components/ui/base/divider';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarationProductTaxes from 'hooks/useDeclarationProductTaxes';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    generateIrelandClearanceDraft,
    generateIrelandSadDraft,
    getIrelandClearanceDraft,
    getIrelandSadDraft,
} from 'store/declarations/client';
import { TaxPaymentMethod, taxPaymentMethodLabels } from 'store/declarations/enums/ireland/tax-payment-method';
import useFormUtils from '../../../../hooks/useFormUtils';
import GrossMassSuggestion from '../GrossMassSuggestions';
import { Alert } from './styles';
import DocumentRow from './DocumentRow';
import useGeneratedDocument from './useGeneratedDocument';
import { Document } from './types';
import ValidationErrorContainer from '../ValidationErrorContainer/ValidationErrorContainer';
import { Col, Row, Space, Typography } from 'antd';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { H2 } from 'components/new-ui/base/typography/styles';
import { useVatDutyCalculations } from '../../declaration-tabs/vat-and-duty/useVatDutyCalculations';
import { getCurrencyData } from 'utils/common';
import GrossMassSumInfo from './GrossMassSumInfo';

interface Props {
    visible: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onToggle: (switched: boolean) => void;
    toggled: boolean;
}

const generateSadDocument = (declarationId: string): Promise<Document> =>
    generateIrelandSadDraft(declarationId).then(() => getIrelandSadDraft(declarationId, 'DRAFT'));

const generateClearanceDocument = (declarationId: string): Promise<Document> =>
    generateIrelandClearanceDraft(declarationId).then(() => getIrelandClearanceDraft(declarationId, 'DRAFT'));

const ModalPreviewSadAndClearanceSlip: FC<Props> = ({ visible, onClose, onConfirm, onToggle, toggled }) => {
    const { declarationId } = useParams();
    const { commodityCodeTaxes, getDeclarationProductTaxes } = useDeclarationProductTaxes(declarationId);
    const sad = useGeneratedDocument(generateSadDocument);
    const clearanceSlip = useGeneratedDocument(generateClearanceDocument);
    const { hasFormDeclarationErrors } = useDeclarationFormErrors();
    const { isAis, isAes, isCdsExportB1, isAisH1 } = useFormUtils();
    const [isValidationErrorsModalOpen, setIsValidationErrorsModalOpen] = useState<boolean>(false);

    const { localData: data, refetch } = useVatDutyCalculations(declarationId, 'local', false);

    const methodOfPayment = useMemo(() => {
        return (
            taxPaymentMethodLabels.find((e) => e.id === commodityCodeTaxes?.methodOfPayment) || { id: '', value: '' }
        );
    }, [commodityCodeTaxes]);

    const { currencySymbol } = getCurrencyData();

    const showDocuments = isAis || isAes || isCdsExportB1;

    useEffect(() => {
        if (declarationId && visible && showDocuments) {
            sad.fetchDocument(declarationId);
            clearanceSlip.fetchDocument(declarationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, declarationId, showDocuments, sad.fetchDocument, clearanceSlip.fetchDocument]);

    // Both sources behind these calculations are Ireland import H1 endpoints, so there is
    // nothing to fetch — or show — for any other declaration type.
    useEffect(() => {
        if (declarationId && visible && isAisH1) {
            getDeclarationProductTaxes();
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, declarationId, isAisH1]);

    const declarationWithErrors = hasFormDeclarationErrors;

    const errors = useMemo(() => {
        if (declarationWithErrors) {
            return (
                <Alert
                    message="This declaration contains errors."
                    type="error"
                    showIcon
                    action={
                        <Button size="small" type="text" onClick={() => setIsValidationErrorsModalOpen(true)}>
                            Details
                        </Button>
                    }
                />
            );
        }
        return <></>;
    }, [declarationWithErrors, setIsValidationErrorsModalOpen]);

    const isPostponedVat = methodOfPayment.id === TaxPaymentMethod.DEFERRED_OR_POSTPONED_PAYMENT;

    return (
        <>
            <ConfirmationModal
                title={
                    <>
                        <Typography.Text
                            style={{ fontWeight: 600, color: 'var(--brand-9)', fontSize: 20, lineHeight: '28px' }}
                        >
                            Pre-Submission Preview
                        </Typography.Text>
                        {errors}
                    </>
                }
                confirmButtonText="Submit"
                confirmationButtonDisabled={declarationWithErrors}
                open={visible}
                type={declarationWithErrors ? 'error' : 'default'}
                icon={declarationWithErrors ? <InfoCircleOutlined /> : <FileSearchOutlined />}
                onCancel={onClose}
                onConfirm={onConfirm}
                width={800}
                content={
                    <div style={{ width: '100%' }}>
                        {showDocuments && (
                            <>
                                <H2>Declaration Documents</H2>
                                <Typography style={{ marginTop: 6 }}>
                                    You can now view the SAD and Clearance slip documents.
                                </Typography>
                                <Typography>Please make sure everything is correct before you confirm.</Typography>
                                <div style={{ color: 'var(--brand-6)', marginTop: 6 }}>
                                    <DocumentRow
                                        label="SAD"
                                        defaultFileName="SADdocument"
                                        state={sad}
                                        onRefresh={() => declarationId && sad.fetchDocument(declarationId)}
                                    />
                                    <DocumentRow
                                        label="Clearance slip"
                                        defaultFileName="Clearanceslip"
                                        state={clearanceSlip}
                                        onRefresh={() => declarationId && clearanceSlip.fetchDocument(declarationId)}
                                    />
                                </div>
                            </>
                        )}

                        <GrossMassSumInfo />
                        <GrossMassSuggestion withSwitch onToggle={onToggle} toggled={toggled} />

                        {isAisH1 && (
                            <>
                                <Divider />
                                <H2>Local VAT & Duty Calculations</H2>
                                <Row gutter={24} style={{ marginTop: 6 }}>
                                    <Col md={12} sm={24}>
                                        <Space style={{ width: '100%' }} direction="vertical" size={6}>
                                            <Row
                                                style={{
                                                    gap: 6,
                                                }}
                                                justify="start"
                                            >
                                                {/* TODO: Waiting for clarification. Agreed with David to hide these values until we have the required information */}
                                                {/* Isn't country of origin tied to item itselt - and could not we have items with different countries of origin */}
                                                <span>Country of Origin: </span>
                                                <strong>-</strong>
                                            </Row>
                                            <Row
                                                justify="start"
                                                style={{
                                                    gap: 6,
                                                }}
                                            >
                                                {/* TODO: Waiting for clarification. Agreed with David to hide these values until we have the required information */}
                                                {/* Isn't the procedure code tied to the item itself and could it be different for each item */}
                                                <span>Procedure Code: </span>
                                                <strong>-</strong>
                                            </Row>
                                            <Row
                                                justify="start"
                                                style={{
                                                    gap: 6,
                                                }}
                                            >
                                                <span>Postponed VAT: </span>
                                                {isPostponedVat ? (
                                                    <CheckCircleFilled
                                                        style={{ fontSize: 22, color: 'var(--success-normal)' }}
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </Row>
                                        </Space>
                                    </Col>
                                    <Col md={12} sm={24}>
                                        <Space style={{ width: '100%' }} direction="vertical" size={6}>
                                            <Row justify="space-between">
                                                <span>Number of Items: </span>
                                                <strong>{data.numberOfItems}</strong>
                                            </Row>
                                            <Row justify="space-between">
                                                <span>Total Duty: </span>
                                                <strong>
                                                    {currencySymbol}
                                                    {data.totalDuty}
                                                </strong>
                                            </Row>
                                            <Row justify="space-between">
                                                <span>Total VAT: </span>
                                                <strong>
                                                    {currencySymbol}
                                                    {data.totalVat}
                                                </strong>
                                            </Row>
                                        </Space>
                                    </Col>
                                </Row>
                                <Divider />

                                <H2>Totals</H2>
                                <Row gutter={24} style={{ marginTop: 6 }}>
                                    <Col md={12} sm={24}>
                                        <Space style={{ width: '100%' }} direction="vertical" size={6}>
                                            <Row style={{ gap: 6 }} justify="start">
                                                <span>Method of Payment: </span>
                                                <strong>{`${methodOfPayment?.id} - ${methodOfPayment?.value}`}</strong>
                                            </Row>
                                            <Row style={{ gap: 6 }} justify="start">
                                                <span>Deferred Payment: </span>
                                                <strong>{commodityCodeTaxes?.customsDutyPayer}</strong>
                                            </Row>
                                        </Space>
                                    </Col>
                                    <Col md={12} sm={24}>
                                        <Space style={{ width: '100%' }} direction="vertical" size={6}>
                                            <Row justify="space-between">
                                                <span>Total Statistical Value: </span>
                                                <strong>
                                                    {currencySymbol}
                                                    {commodityCodeTaxes?.totalStatisticalValue?.toFixed(2) ||
                                                        data.totalStatisticalValue}
                                                </strong>
                                            </Row>
                                            <Row justify="space-between">
                                                <span>Total Liability: </span>
                                                <strong>
                                                    {currencySymbol}
                                                    {data.totalLiability}
                                                </strong>
                                            </Row>
                                        </Space>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>
                }
            />

            <ValidationErrorContainer
                isOpen={isValidationErrorsModalOpen}
                close={() => setIsValidationErrorsModalOpen(false)}
                onNavigate={onClose}
            />
        </>
    );
};

export default ModalPreviewSadAndClearanceSlip;
